/**
 * Teddly — DIWALIOFFER cart upsell.
 *
 * When the cart holds exactly one plush, the customer is one add away from the
 * DIWALIOFFER combo discount (see rakhi-bundle-discount.js).
 * This surfaces that gap in rupees: an always-on nudge in the cart drawer footer,
 * plus one intercept per drawer open when they reach for Checkout.
 *
 * Config comes from #rakhi-combo-offer-config (rendered by cart-drawer.liquid),
 * which is only present when the `show_rakhi_combo_offer` theme setting is on —
 * absent element means the whole feature is off.
 *
 * Must load after cart.js but BEFORE rakhi-bundle-discount.js. Both wrap
 * window.renderCart, and the discount script re-renders through its own inner
 * reference after a discount sync — so if this wrapped it from the outside, the
 * nudge would keep quoting the pre-sync total. Dropping back to one plush is
 * exactly that path: the discount comes off, the total jumps, the nudge must follow.
 */
(function () {
  'use strict';

  var config = document.getElementById('rakhi-combo-offer-config');
  if (!config) return;

  var PLUSH_URL = config.dataset.plushUrl || '/collections/all';
  var COMBO_RATE = 0.10;
  var PREPAID_RATE = 0.05;

  var GROUP_PROP = '_package_group_id';

  // Two full-size plush units unlock the discount. Quantity counts, so one plush
  // bought twice qualifies. Mirrors MIN_PLUSH_UNITS in rakhi-bundle-discount.js,
  // which gates the real discount code — keep the two in step.
  var MIN_PLUSH_UNITS = 2;

  var COPY = {
    href: PLUSH_URL,
    nudge: 'by adding 1 more plush',
    cta: 'Add a Plush',
    why: 'Your cart has one plush. Add one more and 10% comes off your whole cart — bag charms don’t count toward the pair.'
  };

  // Armed on every cart drawer open, spent on the first Checkout click.
  var interceptArmed = false;

  // Bag charms ride along with the discount but never count toward the pair, and
  // neither do the retired Rakhi package lines. Mirrors plushUnits() in
  // rakhi-bundle-discount.js, which gates the real code.
  function plushUnits(cart) {
    return (cart.items || []).reduce(function (n, item) {
      if (item.properties && item.properties[GROUP_PROP]) return n;
      var type = (item.product_type || '').trim().toLowerCase();
      if (type === 'bag charms') return n;
      if (type.indexOf('rakhi') !== -1) return n;
      return n + (item.quantity || 0);
    }, 0);
  }

  /**
   * True only when the cart is exactly one plush short. A charms-only cart is two
   * short, so no single add earns the saving we would be quoting — stay quiet there
   * rather than promise it.
   */
  function needsMorePlush(cart) {
    if (!cart || !cart.item_count) return false;
    return plushUnits(cart) === MIN_PLUSH_UNITS - 1;
  }

  // The 5% prepaid cut applies to what's left after the 10% combo discount, so the
  // two don't simply add to 15% — quote the real number, not the headline.
  function savings(totalPaise) {
    var combo = Math.round(totalPaise * COMBO_RATE);
    var prepaid = Math.round((totalPaise - combo) * PREPAID_RATE);
    return { combo: combo, prepaid: prepaid, total: combo + prepaid };
  }

  function rupees(paise) {
    return '₹' + Math.round(paise / 100).toLocaleString('en-IN');
  }

  // ── Inline nudge in the drawer footer ────────────────────────────────────
  function renderNudge(cart) {
    var slot = document.getElementById('rakhi-combo-nudge');
    if (!slot) return;

    if (!needsMorePlush(cart)) {
      slot.classList.add('hidden');
      slot.innerHTML = '';
      return;
    }

    var copy = COPY;
    var s = savings(cart.total_price);
    slot.classList.remove('hidden');
    // Deliberately two lines tall: the drawer footer is the tightest space on
    // mobile, and this sits between the subtotal and the checkout button.
    slot.innerHTML =
      '<a href="' + copy.href + '" class="rco-nudge">' +
        '<span class="rco-nudge-icon">' +
          '<svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">' +
            '<path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>' +
          '</svg>' +
        '</span>' +
        '<span class="rco-nudge-body">' +
          // Amount first: on a 320px screen the line truncates, and the saving
          // is the one part that must survive. Breakdown lives in the intercept.
          '<span class="rco-nudge-title">Save ' + rupees(s.total) + ' ' + copy.nudge + '</span>' +
          '<span class="rco-nudge-sub">10% off cart + 5% prepaid</span>' +
        '</span>' +
        '<span class="rco-nudge-arrow" aria-hidden="true">' +
          '<svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">' +
            '<path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14m0 0l-6-6m6 6l-6 6"/>' +
          '</svg>' +
        '</span>' +
      '</a>';
  }

  // ── Checkout intercept ───────────────────────────────────────────────────
  function showIntercept(cart, proceed) {
    var copy = COPY;
    var s = savings(cart.total_price);
    var overlay = document.createElement('div');
    overlay.className = 'rco-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.innerHTML =
      '<div class="rco-modal">' +
        '<span class="rco-modal-icon">' +
          '<svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="#fff" stroke-width="2">' +
            '<path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>' +
          '</svg>' +
        '</span>' +
        '<h2 class="rco-modal-title">Wait — ' + rupees(s.total) + ' is still yours</h2>' +
        '<p class="rco-modal-sub">' + copy.why + '</p>' +
        '<div class="rco-modal-math">' +
          '<div class="rco-math-row"><span>10% off entire cart</span><span>−' + rupees(s.combo) + '</span></div>' +
          '<div class="rco-math-row"><span>5% extra on prepaid</span><span>−' + rupees(s.prepaid) + '</span></div>' +
          '<div class="rco-math-row rco-math-total"><span>You save</span><span>' + rupees(s.total) + '</span></div>' +
        '</div>' +
        '<a href="' + copy.href + '" class="rco-modal-cta">' + copy.cta + '</a>' +
        '<button type="button" class="rco-modal-skip">No thanks, checkout without the discount</button>' +
      '</div>';

    document.body.appendChild(overlay);

    function close() {
      overlay.remove();
      document.removeEventListener('keydown', onKey);
    }
    function onKey(e) { if (e.key === 'Escape') close(); }

    overlay.querySelector('.rco-modal-skip').addEventListener('click', function () {
      close();
      proceed();
    });
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });
    document.addEventListener('keydown', onKey);
  }

  // ── Wrapping ─────────────────────────────────────────────────────────────
  function wrapRenderCart() {
    if (typeof window.renderCart !== 'function' || window.renderCart.__rcoWrapped) return;
    var original = window.renderCart;
    var wrapped = function (cart) {
      original(cart);
      renderNudge(cart);
    };
    wrapped.__rcoWrapped = true;
    window.renderCart = wrapped;
  }

  function wrapHandleCheckout() {
    if (typeof window.handleCheckout !== 'function' || window.handleCheckout.__rcoWrapped) return;
    var original = window.handleCheckout;
    var wrapped = function () {
      var args = arguments;
      var self = this;
      var proceed = function () { return original.apply(self, args); };

      if (!interceptArmed) return proceed();

      return fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (!needsMorePlush(cart)) return proceed();
          interceptArmed = false;
          showIntercept(cart, proceed);
        })
        .catch(proceed);
    };
    wrapped.__rcoWrapped = true;
    window.handleCheckout = wrapped;
    // cart.js (and rakhi-bundle-discount.js) already bound the previous function
    // reference to the button — point it at this wrapper instead.
    var checkoutBtn = document.getElementById('checkout-btn');
    if (checkoutBtn) {
      checkoutBtn.removeEventListener('click', original);
      checkoutBtn.addEventListener('click', wrapped);
    }
  }

  function boot() {
    wrapRenderCart();
    wrapHandleCheckout();

    // Every fresh look at the cart earns one ask, right up until checkout is initiated.
    document.addEventListener('cart:open', function () { interceptArmed = true; });

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(renderNudge)
      .catch(function () {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
